import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { RealtimeChannel } from '@supabase/supabase-js';
import type { UptimeCheck } from './types';

interface UptimeRealtimeCallbacks {
  onNewCheck?: (check: UptimeCheck) => void;
  onNewEvent?: (event: { id: string; site_id: string; status: string; created_at: string }) => void;
  onSiteUpdate?: (fields: Record<string, unknown>) => void;
}

export function useUptimeRealtime(siteId: string | undefined, callbacks: UptimeRealtimeCallbacks) {
  const cbRef = useRef(callbacks);
  cbRef.current = callbacks;

  useEffect(() => {
    if (!siteId) return;

    const channel: RealtimeChannel = supabase
      .channel(`uptime-realtime:${siteId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'site_uptime_checks',
          filter: `site_id=eq.${siteId}`,
        },
        (payload) => {
          cbRef.current.onNewCheck?.(payload.new as UptimeCheck);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'workspace_site_uptime_events',
          filter: `site_id=eq.${siteId}`,
        },
        (payload) => {
          cbRef.current.onNewEvent?.(payload.new as any);
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'workspace_sites',
          filter: `id=eq.${siteId}`,
        },
        (payload) => {
          cbRef.current.onSiteUpdate?.(payload.new as Record<string, unknown>);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [siteId]);
}
